import React, { useState } from 'react';
import { LoaderCircle } from 'lucide-react';

import {
  IMAGE_LANGUAGES,
  assignProposalLanguages,
  normalizeImageSlot,
  removeImageProposal,
} from './contentLayout';


const LANGUAGE_LABELS = {
  fr: 'Français',
  en: 'Anglais',
  pt: 'Portugais',
};

const LANGUAGE_TARGET_ARIA = {
  fr: 'le français',
  en: 'l’anglais',
  pt: 'le portugais',
};

const CAPTION_ARIA = {
  fr: 'française',
  en: 'anglaise',
  pt: 'portugaise',
};

const generatedToken = () => {
  const raw =
    window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  return String(raw).replace(/[^A-Za-z0-9-]/g, '').slice(0, 64);
};

export const createProposalId = () => `proposal_${generatedToken()}`;

export const hasUsableImageUrl = (value) =>
  typeof value === 'string' &&
  Boolean(value.trim()) &&
  !/^(javascript|data|vbscript|file):/i.test(value.trim());

const ImagePreview = ({ proposal, proposalNumber, broken, onError }) => {
  if (!hasUsableImageUrl(proposal.url)) {
    return <span className="image-manager-preview-error">Entrée d’image invalide</span>;
  }
  if (broken) {
    return <span className="image-manager-preview-error">Image introuvable</span>;
  }
  return (
    <img
      src={proposal.url}
      alt={`Aperçu proposition ${proposalNumber}`}
      onError={onError}
    />
  );
};

const ImageProposalEditor = ({
  slot: slotValue,
  onChange,
  onUploadImage,
  canEdit = false,
  view = 'all',
  selectedProposalId,
  onSelectProposal,
}) => {
  const slot = normalizeImageSlot(slotValue);
  const [uploadError, setUploadError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [brokenPreviews, setBrokenPreviews] = useState({});

  const emitChange = (nextSlot) => {
    if (onChange) onChange(nextSlot);
  };

  const updateCaption = (proposalId, language, value) => {
    emitChange({
      ...slot,
      proposals: slot.proposals.map((proposal) =>
        proposal.id === proposalId
          ? {
              ...proposal,
              captions: { ...proposal.captions, [language]: value },
            }
          : proposal
      ),
    });
  };

  const toggleLanguage = (proposalId, language) => {
    const assignedLanguages = IMAGE_LANGUAGES.filter(
      (item) => slot.active_proposal_ids[item] === proposalId
    );
    const active = slot.active_proposal_ids[language] === proposalId;
    const nextLanguages = active
      ? assignedLanguages.filter((item) => item !== language)
      : [...assignedLanguages, language];
    emitChange(assignProposalLanguages(slot, proposalId, nextLanguages));
  };

  const uploadProposals = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0 || !onUploadImage) return;
    setUploadError('');
    setUploadProgress({ current: 1, total: files.length });
    try {
      const uploadedProposals = [];
      for (const [fileIndex, file] of files.entries()) {
        setUploadProgress({ current: fileIndex + 1, total: files.length });
        const uploaded = await onUploadImage(file);
        const url =
          typeof uploaded === 'string'
            ? uploaded
            : uploaded?.url || uploaded?.data?.url;
        if (!hasUsableImageUrl(url)) throw new Error('invalid image URL');
        uploadedProposals.push({
          id: createProposalId(),
          url,
          captions: { fr: '', en: '', pt: '' },
        });
      }
      emitChange({
        ...slot,
        proposals: [...slot.proposals, ...uploadedProposals],
      });
    } catch {
      setUploadError('Le téléversement a échoué.');
    } finally {
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const isUploading = uploadProgress.total > 0;

  const selectedProposal =
    slot.proposals.find((proposal) => proposal.id === selectedProposalId) ||
    slot.proposals[0] ||
    null;
  const captionProposals =
    view === 'all'
      ? slot.proposals
      : selectedProposal
        ? [selectedProposal]
        : [];
  const showImages = view === 'images' || view === 'all';
  const showCaptions = view === 'captions' || view === 'all';

  return (
    <div className="image-proposal-editor">
      {showImages && (
        <>
          <section className="image-manager-language-summary" aria-label="Affichage actuel">
            <strong>Affichage actuel</strong>
            <div className="image-manager-language-summary-list">
              {IMAGE_LANGUAGES.map((language) => {
                const proposalIndex = slot.proposals.findIndex(
                  (proposal) =>
                    proposal.id === slot.active_proposal_ids[language]
                );
                return (
                  <span key={language}>
                    <b>{language.toUpperCase()}</b>{' '}
                    {proposalIndex >= 0
                      ? slot.proposals.length === 1
                        ? 'Visible'
                        : `Image ${proposalIndex + 1}`
                      : 'Masquée'}
                  </span>
                );
              })}
            </div>
          </section>

          {slot.proposals.length === 0 && (
            <p className="image-manager-empty">Aucune image disponible.</p>
          )}

          <div className="image-manager-proposal-list">
            {slot.proposals.map((proposal, index) => {
              const proposalNumber = index + 1;
              return (
                <article
                  key={proposal.id || `proposal-${index}`}
                  className="image-manager-proposal-card"
                >
                  <div className="image-manager-proposal-preview">
                    <ImagePreview
                      proposal={proposal}
                      proposalNumber={proposalNumber}
                      broken={brokenPreviews[proposal.id]}
                      onError={() =>
                        setBrokenPreviews((current) => ({
                          ...current,
                          [proposal.id]: true,
                        }))
                      }
                    />
                  </div>

                  <div className="image-manager-proposal-content">
                    <div className="image-manager-proposal-heading">
                      <h4>Image {proposalNumber}</h4>
                      {canEdit && (
                        <button
                          type="button"
                          className="image-manager-danger-link"
                          aria-label={`Supprimer la proposition ${proposalNumber}`}
                          onClick={() =>
                            emitChange(removeImageProposal(slot, proposal.id))
                          }
                        >
                          Supprimer cette image
                        </button>
                      )}
                    </div>

                    {canEdit ? (
                      <>
                        <p className="image-manager-field-hint">
                          Afficher cette image pour
                        </p>
                        <div className="image-manager-language-buttons">
                          {IMAGE_LANGUAGES.map((language) => {
                            const active =
                              slot.active_proposal_ids[language] === proposal.id;
                            return (
                              <button
                                key={language}
                                type="button"
                                aria-label={`Utiliser l’image ${proposalNumber} pour ${LANGUAGE_TARGET_ARIA[language]}`}
                                aria-pressed={active}
                                onClick={() =>
                                  toggleLanguage(proposal.id, language)
                                }
                              >
                                {language.toUpperCase()}
                              </button>
                            );
                          })}
                          <button
                            type="button"
                            className="image-manager-all-languages"
                            aria-label={`Afficher la proposition ${proposalNumber} dans toutes les langues`}
                            onClick={() =>
                              emitChange(
                                assignProposalLanguages(
                                  slot,
                                  proposal.id,
                                  IMAGE_LANGUAGES
                                )
                              )
                            }
                          >
                            Toutes les langues
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="image-manager-readonly-captions">
                        {IMAGE_LANGUAGES.map((language) =>
                          proposal.captions?.[language] ? (
                            <p key={language}>
                              {LANGUAGE_LABELS[language]} :{' '}
                              {proposal.captions[language]}
                            </p>
                          ) : null
                        )}
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {canEdit && onUploadImage && (
            <label
              className="image-manager-upload-button"
              aria-disabled={isUploading ? 'true' : undefined}
            >
              {isUploading ? (
                <span
                  className="image-manager-upload-progress"
                  role="status"
                  aria-live="polite"
                >
                  <LoaderCircle
                    className="image-manager-upload-spinner"
                    size={17}
                    aria-hidden="true"
                  />
                  Chargement de l’image {uploadProgress.current} sur{' '}
                  {uploadProgress.total}…
                </span>
              ) : (
                'Ajouter une ou plusieurs images'
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={isUploading}
                className="sr-only"
                aria-label="Ajouter une ou plusieurs images"
                onChange={uploadProposals}
              />
            </label>
          )}
          {uploadError && (
            <p role="alert" className="image-manager-error">
              {uploadError}
            </p>
          )}
        </>
      )}

      {showCaptions && (
        <div className="image-manager-captions-layout">
          {view === 'captions' && slot.proposals.length > 0 && (
            <div className="image-manager-caption-selector" aria-label="Choisir une image">
              {slot.proposals.map((proposal, index) => (
                <button
                  key={proposal.id}
                  type="button"
                  aria-pressed={proposal.id === selectedProposal?.id}
                  onClick={() => onSelectProposal?.(proposal.id)}
                >
                  <span className="image-manager-caption-selector-preview">
                    <ImagePreview
                      proposal={proposal}
                      proposalNumber={index + 1}
                      broken={brokenPreviews[proposal.id]}
                      onError={() =>
                        setBrokenPreviews((current) => ({
                          ...current,
                          [proposal.id]: true,
                        }))
                      }
                    />
                  </span>
                  Image {index + 1}
                </button>
              ))}
            </div>
          )}

          <div className="image-manager-caption-fields">
            {captionProposals.length === 0 ? (
              <p className="image-manager-empty">
                Ajoutez une image avant de saisir ses légendes.
              </p>
            ) : (
              captionProposals.map((proposal) => {
                const proposalNumber =
                  slot.proposals.findIndex((item) => item.id === proposal.id) + 1;
                return (
                  <section key={proposal.id} className="image-manager-caption-group">
                    {view === 'all' && <h4>Image {proposalNumber}</h4>}
                    {IMAGE_LANGUAGES.map((language) => (
                      <label key={language}>
                        <span>{LANGUAGE_LABELS[language]}</span>
                        <input
                          type="text"
                          aria-label={`Légende ${CAPTION_ARIA[language]} proposition ${proposalNumber}`}
                          value={proposal.captions?.[language] || ''}
                          disabled={!canEdit}
                          onChange={(event) =>
                            updateCaption(
                              proposal.id,
                              language,
                              event.target.value
                            )
                          }
                        />
                      </label>
                    ))}
                  </section>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageProposalEditor;
